import React, { useEffect, useState, useCallback } from 'react';
import styles from './MagicUpload.module.scss';
import { useLocation, useSearchParams } from 'react-router-dom';
import DrawerContent from './components/DrawerContent';
import useMagicUploadLogic from './useMagicUploadLogic';
import NeedInfo from '../NeedInfo/NeedInfo';
import { MagicUploadLoading, MagicUploadSelected, needInfoSelected } from '../../assets/hostedassets';
import closeIcon from '../_custom/SideDrawer/close.svg';
import { MAGIC_UPLOAD, MAGIC_SIGN, NEED_INFO } from './Utils/MagicUploadConstants';
import MagicSignIcon from '../../assets/Svg/MagicSignIcon';
import MagicUploadTabSelector from './components/MagicUploadTabSelector/MagicUploadTabSelector';
import MagicSignComponent from '../MagicSign/MagicSign';
import GlobalMagicSign from '../../modules/GlobalMagicSign/GlobalMagicSign';
import { MagicUploadFileData } from './useFetchDataForMU';
import AIText from '../AIText/AIText';
import { useAppSelector } from '../../app/hooks';
import { USER_TYPE } from '../../utils/constants/user';
import NeedInfoV2 from '../NeedInfoV2/NeedInfoV2';
import { SOURCE } from '../NeedInfoV2/NeedInfoV2Constants';
import { useFeatureVisibility } from '../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../utils/constants/visibilityKeys';
import RequirementTabSelector from '../NeedInfoV2/components/RequirementTabSelector/RequirementTabSelector';
import { TAB_STATE, TabState } from '../NeedInfoV2/NeedInfoV2Constants';

type MagicUploadProps = {};
const MagicUpload: React.FC<MagicUploadProps> = (props: MagicUploadProps) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const disableAnimation = searchParams.get('disableAnimation') === 'true';
    const location = useLocation();
    const companyName = searchParams.get('companyName');
    const lenderName = searchParams.get('lenderName');
    const tabStateParam = searchParams.get('tabState') as 'new' | 'tagged' | 'all' | null;
    const [magicUploadTabState, setMagicUploadTabState] = useState<'new' | 'tagged' | 'all'>('all');
    const [requirementTabState, setRequirementTabState] = useState<TabState>(TAB_STATE.ACTIVE);
    const [loading, setLoading] = useState(false);
    const [fileData, setFileData] = useState<MagicUploadFileData[]>([]);
    const [requirementCounts, setRequirementCounts] = useState<{ activeCount: number; archivedCount: number }>({
        activeCount: 0,
        archivedCount: 0,
    });
    const { isVisible, toggleDrawer, drawerContent, changeDrawerContent } = useMagicUploadLogic();
    const { magicUploadVisibility, magicSignVisibility } = useFeatureVisibility({
        magicUploadVisibility: VISIBILITY_KEYS.MAGIC_UPLOAD.ENABLED,
        magicSignVisibility: VISIBILITY_KEYS.MAGIC_SIGN.ENABLED,
    });
    const user = useAppSelector((state) => state.user);
    const isPartner = user.userData.accountType === USER_TYPE.PARTNER;
    const isCompany = user.userData.accountType === USER_TYPE.COMPANY || location.pathname.includes('company');
    const isCompanySide = user.userData.accountType === USER_TYPE.COMPANY;
    // Magic Sign tab context: company-scoped when the drawer carries a company (isCompany=true),
    // global (cross-company, org column) otherwise — same signal MagicUploadFilesTable uses
    const isMagicSignCompanyContext = searchParams.get('isCompany') === 'true';
    const permissions = useAppSelector((state) => state.investor.permissions);
    const showMu = magicUploadVisibility.show;
    const showMagicSign = magicSignVisibility.show;
    const [heading, setHeading] = useState<string>('');

    // Every tab panel below stays MOUNTED once visited so switching tabs preserves state — but a
    // tab the user has never opened must not mount (and fire its on-mount fetches) at all.
    // Seeded with the initial drawerContent so the tab shown first behaves exactly like before
    // (single active panel), then grows as the user visits more tabs.
    const [visitedTabs, setVisitedTabs] = useState<Set<string>>(() => new Set([drawerContent]));
    useEffect(() => {
        setVisitedTabs((prev) => (prev.has(drawerContent) ? prev : new Set(prev).add(drawerContent)));
    }, [drawerContent]);

    const handleCountsUpdate = useCallback((activeCount: number, archivedCount: number) => {
        setRequirementCounts({ activeCount, archivedCount });
    }, []);

    useEffect(() => {
        // Defensive check: companyName can be the string 'null' when URL query params
        // stringify null values incorrectly upstream. This prevents displaying 'null' as heading.
        // TODO: Fix root cause where null values are converted to string 'null' in query params
        if (companyName && companyName !== 'null') {
            setHeading(companyName);
        } else {
            setHeading('');
        }
    }, [companyName, lenderName, isPartner, isCompanySide, isCompany]);

    useEffect(() => {
        if (tabStateParam) {
            setMagicUploadTabState(tabStateParam);
            setSearchParams((currentParams) => {
                currentParams.delete('tabState');
                return currentParams;
            });
        }
    }, [tabStateParam, setSearchParams]);

    return (
        <>
            <div
                data-testid="backdrop"
                className={`${styles.backdrop} ${isVisible ? styles.show : styles.hide} ${disableAnimation ? styles.noAnimation : ''}`}
                onClick={toggleDrawer}
            ></div>
            <div className={`${styles.drawer} ${isVisible ? styles.show : styles.hide} ${disableAnimation ? styles.noAnimation : ''}`}>
                <div className={styles.DrawerHeader}>
                    <div className={styles.DrawerContentSelector}>
                        <div className={styles.DrawerTabsContainer}>
                            {searchParams.get('showNeedInfo') === 'true' && (
                                <div
                                    onClick={() => changeDrawerContent(NEED_INFO)}
                                    className={drawerContent === NEED_INFO ? styles.ActiveDrawerTab : styles.DrawerTab}
                                >
                                    <div className={drawerContent === NEED_INFO ? styles.ActiveTab : styles.NotActiveTab}>
                                        <img src={needInfoSelected} alt="NeedInfo" />
                                        <span className={drawerContent === NEED_INFO ? styles.ActiveText : styles.Text}>
                                            {searchParams.get(SOURCE.LENDER_REQ) === 'true' ? 'Lender Query' : 'Data Requests'}
                                        </span>
                                    </div>
                                </div>
                            )}
                            {showMu && searchParams.get('hideMu') !== 'true' && (
                                <div
                                    style={loading ? { pointerEvents: 'none' } : {}}
                                    onClick={() => changeDrawerContent(MAGIC_UPLOAD)}
                                    className={drawerContent === MAGIC_UPLOAD ? styles.ActiveDrawerTab : styles.DrawerTab}
                                >
                                    <div className={drawerContent === MAGIC_UPLOAD || loading ? styles.ActiveTab : styles.NotActiveTab}>
                                        <img
                                            src={
                                                drawerContent === MAGIC_UPLOAD
                                                    ? MagicUploadSelected
                                                    : loading
                                                    ? MagicUploadLoading
                                                    : MagicUploadSelected
                                            }
                                            alt="NeedInfo"
                                        />
                                        <span className={drawerContent === MAGIC_UPLOAD ? styles.ActiveText : styles.Text}>
                                            {loading && drawerContent !== MAGIC_UPLOAD ? (
                                                <AIText text={'Uploading'} />
                                            ) : isPartner ? (
                                                'Upload Data'
                                            ) : (
                                                'Magic Upload'
                                            )}
                                        </span>
                                    </div>
                                </div>
                            )}
                            {showMagicSign && (
                                <div
                                    onClick={() => changeDrawerContent(MAGIC_SIGN)}
                                    className={drawerContent === MAGIC_SIGN ? styles.ActiveDrawerTab : styles.DrawerTab}
                                    data-testid="magic-sign-tab"
                                >
                                    <div className={drawerContent === MAGIC_SIGN ? styles.ActiveTab : styles.NotActiveTab}>
                                        <MagicSignIcon
                                            colour={drawerContent === MAGIC_SIGN ? 'var(--primary-text-colour)' : 'var(--icon-color)'}
                                            width="16"
                                            height="16"
                                        />
                                        <span className={drawerContent === MAGIC_SIGN ? styles.ActiveText : styles.Text}>Magic Sign</span>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div
                            className={`${styles.TabSlideRightContianer} ${
                                drawerContent === MAGIC_UPLOAD || drawerContent === NEED_INFO || drawerContent === MAGIC_SIGN
                                    ? styles.showTabs
                                    : styles.hideTabs
                            } ${disableAnimation ? styles.noAnimation : ''}`}
                        >
                            {drawerContent === MAGIC_UPLOAD ? (
                                <MagicUploadTabSelector tabState={magicUploadTabState} setTabState={setMagicUploadTabState} />
                            ) : drawerContent === NEED_INFO && !isPartner && !isCompanySide ? (
                                <RequirementTabSelector
                                    tabs={[
                                        {
                                            id: TAB_STATE.ACTIVE,
                                            label: 'Active',
                                            count: requirementCounts.activeCount,
                                        },
                                        {
                                            id: TAB_STATE.ARCHIVED,
                                            label: 'Archived',
                                            count: requirementCounts.archivedCount,
                                        },
                                    ]}
                                    activeTabId={requirementTabState}
                                    onTabChange={(tabId) => setRequirementTabState(tabId as TabState)}
                                />
                            ) : null}
                        </div>
                    </div>
                    <div className={styles.heading}>
                        <span>{heading}</span>
                    </div>
                    <div className={styles.closeIcon}>
                        <img onClick={() => toggleDrawer()} src={closeIcon} alt="close" />
                    </div>
                </div>
                {/* All three tab contents stay MOUNTED once visited (visibility toggled via CSS class to
                    preserve state on tab switch) — but a tab never visited is not mounted at all, so
                    NeedInfoV2 / Magic Upload's DrawerContent don't fire wasted fetches on e.g. the
                    Magic Sign tab. Magic Sign additionally gates its own internal fetch via isActive. */}
                <div className={drawerContent === NEED_INFO ? styles.TabPanelVisible : styles.TabPanelHidden}>
                    {visitedTabs.has(NEED_INFO) && (
                        <NeedInfoV2
                            setLoading={setLoading}
                            toggleDrawer={toggleDrawer}
                            tabState={requirementTabState}
                            fileData={fileData}
                            setFileData={setFileData}
                            hasEditPermission={!isCompany}
                            onCountsUpdate={handleCountsUpdate}
                        />
                    )}
                </div>
                <div className={drawerContent === MAGIC_SIGN ? styles.TabPanelVisible : styles.TabPanelHidden}>
                    {/* isActive gates the initial fetch — fetches only on first tab activation, then preserves state */}
                    {visitedTabs.has(MAGIC_SIGN) &&
                        (isMagicSignCompanyContext ? (
                            <MagicSignComponent isActive={drawerContent === MAGIC_SIGN} />
                        ) : (
                            <GlobalMagicSign isActive={drawerContent === MAGIC_SIGN} />
                        ))}
                </div>
                <div className={drawerContent === MAGIC_UPLOAD ? styles.TabPanelVisible : styles.TabPanelHidden}>
                    {visitedTabs.has(MAGIC_UPLOAD) && (
                        <DrawerContent
                            setFileData={setFileData}
                            fileData={fileData}
                            tabState={magicUploadTabState}
                            toggleDrawer={toggleDrawer}
                        />
                    )}
                </div>
            </div>
        </>
    );
};

export default MagicUpload;
